import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Radio, Select, message, Upload } from 'antd';
import type { UploadFile } from 'antd';
import { MessageOutlined, InboxOutlined } from '@ant-design/icons';
import { partnerAPI } from '../services/api';

const { TextArea } = Input;

// 화면 카운터 = 백엔드 검증 기준 (main.py INQUIRY_TITLE_MAX / INQUIRY_CONTENT_MAX)
export const TITLE_MAX = 300;
export const CONTENT_MAX = 2000;

interface Props {
  open: boolean;
  onCancel: () => void;
  /** 등록 성공 후 호출 (목록 갱신용) */
  onCreated?: () => void;
  /** 지정 시 대상 시험 고정 (선택 UI 없이 자동 등록) */
  scheduleId?: number | null;
  scheduleLabel?: string;
}

/** 문의/요청 작성 모달 — 문의 화면·시험일정 목록/상세 어디서든 공용 사용 */
const InquiryWriteModal: React.FC<Props> = ({ open, onCancel, onCreated, scheduleId, scheduleLabel }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setFileList([]);
    form.setFieldsValue({ inquiry_type: '문의', schedule_id: scheduleId ?? undefined });
    // 대상 시험이 고정되지 않은 경우에만 선택 목록 로드
    if (!scheduleId && schedules.length === 0) {
      partnerAPI.schedules()
        .then(res => setSchedules(res.data?.items || res.data || []))
        .catch(() => { /* 목록 없이도 일반 문의 등록 가능 */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scheduleId]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const res = await partnerAPI.createInquiry({
        schedule_id: scheduleId ?? values.schedule_id,
        inquiry_type: values.inquiry_type,
        title: values.title,
        content: values.content,
      });
      // 문의 등록 후 첨부파일 순차 업로드 (일부 실패해도 문의는 유지)
      const failed: string[] = [];
      for (const f of fileList) {
        const raw = (f.originFileObj || f) as File;
        try {
          await partnerAPI.uploadInquiryFile(res.data.id, raw);
        } catch {
          failed.push(f.name);
        }
      }
      if (failed.length) {
        message.warning(`문의는 접수되었으나 첨부 실패: ${failed.join(', ')}`);
      } else {
        message.success('문의가 접수되었습니다. 담당자 확인 후 답변드립니다.');
      }
      onCancel();
      onCreated?.();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.detail || '등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={<><MessageOutlined style={{ marginRight: 8, color: '#f59e0b' }} />문의 / 요청 작성</>}
      open={open}
      onCancel={onCancel}
      onOk={submit}
      confirmLoading={submitting}
      okText="접수"
      cancelText="취소"
      width={560}
      maskClosable={false}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        {/* 자기분류 신뢰도 확보 — 경계가 모호하면 오분류가 담당자 큐를 오염시킨다 */}
        <Form.Item
          name="inquiry_type" label="구분" rules={[{ required: true }]}
          style={{ marginBottom: 12 }}
          extra={
            <span style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.6, display: 'block', marginTop: 4 }}>
              <b style={{ color: '#3b82f6' }}>문의</b> — 답변만 필요한 질문 (진행 상황·일정 확인 등)<br />
              <b style={{ color: '#8b5cf6' }}>요청</b> — 조치가 필요한 건 (일정 변경, 성적서 재발급 등) · 담당자가 <b>수용/반려</b>로 결과를 회신합니다
            </span>
          }
        >
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value="문의">문의</Radio.Button>
            <Radio.Button value="요청">요청</Radio.Button>
          </Radio.Group>
        </Form.Item>
        {scheduleId ? (
          <Form.Item label="대상 시험">
            <Input value={scheduleLabel || `#${scheduleId}`} disabled />
          </Form.Item>
        ) : (
          <Form.Item name="schedule_id" label="대상 시험 (선택)">
            <Select
              allowClear showSearch placeholder="시험을 선택하세요 (미선택 시 일반 문의)"
              optionFilterProp="label"
              options={schedules.map((s: any) => ({
                value: s.id,
                label: `${s.test_number || ''} · ${s.title || ''}`,
              }))}
            />
          </Form.Item>
        )}
        <Form.Item
          name="title" label="제목"
          rules={[
            { required: true, message: '제목을 입력하세요' },
            { max: TITLE_MAX, message: `제목은 ${TITLE_MAX}자까지 입력할 수 있습니다` },
          ]}
        >
          <Input placeholder="예) 3차 조사 일정 조정 요청" maxLength={TITLE_MAX} showCount />
        </Form.Item>
        <Form.Item
          name="content" label="내용"
          rules={[
            { required: true, message: '내용을 입력하세요' },
            { max: CONTENT_MAX, message: `내용은 ${CONTENT_MAX}자까지 입력할 수 있습니다` },
          ]}
        >
          <TextArea rows={6} placeholder="문의 또는 요청 내용을 입력하세요." maxLength={CONTENT_MAX} showCount />
        </Form.Item>
        <Form.Item label="첨부파일" style={{ marginBottom: 0 }}>
          <Upload.Dragger
            multiple
            fileList={fileList}
            beforeUpload={(file) => {
              if (file.size > 20 * 1024 * 1024) {
                message.error(`${file.name}: 20MB를 초과할 수 없습니다.`);
                return Upload.LIST_IGNORE;
              }
              return false;   // 자동 업로드 비활성 — 문의 등록 후 일괄 전송
            }}
            onChange={({ fileList: fl }) => setFileList(fl)}
            onRemove={(file) => setFileList((prev) => prev.filter((f) => f.uid !== file.uid))}
            style={{ padding: '10px 8px', background: '#fffbeb', borderColor: '#fde68a' }}
          >
            <p style={{ margin: 0, fontSize: 22, lineHeight: 1, color: '#f59e0b' }}>
              <InboxOutlined />
            </p>
            <p style={{ margin: '6px 0 2px', fontSize: 13, color: '#475569' }}>
              파일을 이곳에 끌어다 놓거나 <b style={{ color: '#b45309' }}>클릭하여 선택</b>
            </p>
            <p style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>
              파일당 최대 20MB · 여러 개 동시 첨부 가능
            </p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default InquiryWriteModal;
